import axios, { AxiosResponse } from 'axios';
import { EntityLabel } from '@esg/esg-global-types';
import { API_ENDPOINT, CLOUD_FUNCTION_URL, GCP_RESOURCE_UPLOAD_BUCKET } from '../../env';
import { getAuthHeaderForUser } from '../auth/auth.util';

interface FileTypeByExtensionsMap {
  [file_type: string]: Array<string>;
}

// Singular and plural label for resource category entity.
export const resource_category_label: EntityLabel = {
  one: 'Resource Category',
  many: 'Resource Categories'
};

// Singular and plural label for resource entity.
export const resource_label: EntityLabel = {
  one: 'Resource',
  many: 'Resources'
};

/**
 * Uploads a video resource file directly to GCS using a V4 signed upload URL from the Cloud Function.
 * @param {string} resource_name The resource name (used as the GCS directory).
 * @param {File} file The video file to upload.
 * @returns {Promise<string>} The GCS object path of the uploaded file (without bucket prefix).
 */
export const uploadVideoResourceDirectToGcs = async (
  resource_name: string,
  file: File
): Promise<string> => {
  const sanitized_resource_name: string = resource_name.replace(/ /g, '_').toLowerCase();
  const sanitized_file_name: string = file.name.replace(/ /g, '_').toLowerCase();
  const destination_path = `${sanitized_resource_name}/${sanitized_file_name}`;
  const content_type: string = file.type || 'video/mp4';

  // Get signed upload URL for cloud storage.
  const signed_url_response: AxiosResponse = await axios({
    method: 'post',
    url: `${CLOUD_FUNCTION_URL}/getSignedFileUrls`,
    data: {
      action: 'write',
      bucket: GCP_RESOURCE_UPLOAD_BUCKET,
      path: destination_path,
      content_type
    },
    headers: { ...(await getAuthHeaderForUser()) }
  });

  const upload_url: string = signed_url_response.data.data.urls[0].url;

  // Upload video resource directly to cloud storage.
  await axios({
    method: 'put',
    url: upload_url,
    data: file,
    headers: { 'Content-Type': content_type }
  });

  return destination_path;
};

/**
 * Initiates a GCP Video Transcoder job for a resource via the backend API.
 * @param {string} resource_id The Firestore ID of the resource to transcode.
 * @param {string} resource_upload_url The full GCS object path to the uploaded video file (no gs:// prefix).
 * @returns {Promise<void>}
 */
export const transcodeVideoResource = async (
  resource_id: string,
  resource_upload_url: string
): Promise<void> => {
  const input_uri = `gs://${GCP_RESOURCE_UPLOAD_BUCKET}/${resource_upload_url}`;
  const resource_dir: string = resource_upload_url.split('/').slice(0, -1).join('/');
  const output_uri = `gs://${GCP_RESOURCE_UPLOAD_BUCKET}/${resource_dir}/hls/`;
  await axios({
    method: 'post',
    url: `${API_ENDPOINT}/transcode_video`,
    data: { resource_id, input_uri, output_uri },
    headers: {
      ...(await getAuthHeaderForUser())
    }
  });
};

/**
 * Generates a human-readable file type label from a file extension.
 * Labels match the RESOURCE_FILE_TYPES accepted by the v2 API validator.
 * @param {string} file_extension File extension including leading dot (e.g. '.pdf')
 * @returns {string} Human-readable file type label, or 'Unknown File' if unrecognised
 */
export const getResourceFileTypeByExtension = (file_extension: string): string => {
  const file_types_by_extensions: FileTypeByExtensionsMap = {
    Image: ['.jpg', '.png', '.webp', '.gif'],
    Excel: ['.xls', '.xlsx'],
    Audio: ['.mp3', '.wav', '.aac', '.flac'],
    Video: ['.mp4', '.avi'],
    CSV: ['.csv'],
    'Text File': ['.txt', '.rtf', '.html'],
    'Word Doc': ['.doc', '.docx'],
    'Power Point': ['.ppt', '.pptx'],
    PDF: ['.pdf']
  };

  for (const key of Object.keys(file_types_by_extensions)) {
    if (file_types_by_extensions[key].includes(file_extension)) return key;
  }

  return 'Unknown File';
};
